import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, FileDown } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useIsMobile, optimizedImg } from "@/lib/media";
import { useSettings } from "@/lib/settings";
import { getGuest } from "@/lib/guest";
import GuestDetailsDialog from "@/components/site/GuestDetailsDialog";
import LogoMark from "@/components/site/LogoMark";

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII", "XIII", "XIV", "XV", "XVI", "XVII", "XVIII", "XIX", "XX"];
const roman = (n) => ROMAN[n - 1] || String(n);

// Individual photo page — uses server-side resized WEBP + a subtle skeleton shimmer while loading
function PhotoPage({ p, enhanceCls, imgWidth, imgQ }) {
  const [loaded, setLoaded] = useState(false);
  const src = optimizedImg(fileUrl(p.url), imgWidth, imgQ);
  return (
    <div className="h-full p-2.5 md:p-4 flex flex-col">
      <div className="flex-1 overflow-hidden bg-bone relative">
        {!loaded && (
          <div className="absolute inset-0 album-shimmer" aria-hidden="true" />
        )}
        <img
          src={src}
          alt={`${p.category} ${p.index}`}
          className={`w-full h-full object-cover ${enhanceCls} transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
          loading="eager"
          decoding="async"
          fetchPriority="high"
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
        />
        <div className="absolute inset-0 pointer-events-none shadow-[inset_0_0_28px_rgba(26,26,26,0.10)]" />
      </div>
      <div className="flex justify-between items-center pt-2 text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-graphite">
        <span>{p.category}</span>
        <span>{p.index} / {p.total}</span>
      </div>
    </div>
  );
}

export default function AlbumBook({ title = "", slug = "", categories = [], enhance = false, endHeading = "", endMessage = "", endFooter = "" }) {
  const { settings } = useSettings();
  const isMobile = useIsMobile();
  const cats = useMemo(() => categories.filter((c) => (c.images || []).filter(Boolean).length), [categories]);

  const pages = useMemo(() => {
    const p = [{ type: "cover" }];
    cats.forEach((c, ci) => {
      const imgs = (c.images || []).filter(Boolean);
      const name = c.name || "Untitled";
      p.push({ type: "chapter", name, num: ci + 1, count: imgs.length, msg: (c.intro_text || "").trim() });
      imgs.forEach((url, i) => p.push({ type: "photo", url, category: name, index: i + 1, total: imgs.length }));
      if ((c.outro_text || "").trim()) p.push({ type: "note", name, msg: c.outro_text.trim() });
    });
    p.push({ type: "end" });
    return p;
  }, [cats]);

  const perView = isMobile ? 1 : 2;
  const spreads = Math.max(Math.ceil(pages.length / perView), 1);
  const [spread, setSpread] = useState(0);
  const [turnKey, setTurnKey] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);
  const touchX = useRef(null);

  useEffect(() => { setSpread(0); }, [perView]);

  const go = (dir) => {
    setSpread((s) => {
      const n = Math.min(Math.max(s + dir, 0), spreads - 1);
      if (n !== s) setTurnKey((k) => k + 1);
      return n;
    });
  };
  const jumpToCat = (name) => {
    const idx = pages.findIndex((p) => p.type === "chapter" && p.name === name);
    if (idx >= 0) { setSpread(Math.floor(idx / perView)); setTurnKey((k) => k + 1); }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spreads]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
  };

  const requestDownload = () => {
    const g = getGuest();
    if (g) downloadPdf(g);
    else setGuestOpen(true);
  };
  const downloadPdf = async (guest) => {
    setDownloading(true);
    try {
      const res = await api.post(
        "/public/album-pdf",
        { property_slug: slug || "", title, categories: cats, enhance: !!enhance, end_heading: endHeading || "", end_message: endMessage || "", end_footer: endFooter || "", name: guest?.name || "", phone: guest?.phone || "", email: guest?.email || "" },
        { responseType: "blob", timeout: 120000 }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Happy-Retreats-Album${slug ? `-${slug}` : ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your album PDF is downloading — share it with the family!");
    } catch (err) {
      const status = err?.response?.status;
      toast.error(status
        ? `Could not prepare the album PDF (error ${status}). Please try again.`
        : "Could not prepare the album PDF. Please check your connection and try again.");
    } finally {
      setDownloading(false);
    }
  };

  // Image width served by /api/img — big enough for retina but small enough for fast mobile
  const imgWidth = isMobile ? 800 : 1400;
  const imgQ = isMobile ? 68 : 72;
  // Preload nearby photo pages (2 behind, 6 ahead) so page-turns feel instant
  const preloadUrls = useMemo(() => {
    const out = [];
    const start = Math.max(spread * perView - perView * 2, 0);
    for (let i = start; i < Math.min(spread * perView + perView * 6, pages.length); i++) {
      const p = pages[i];
      if (p && p.type === "photo" && p.url) out.push(optimizedImg(fileUrl(p.url), imgWidth, imgQ));
    }
    return out;
  }, [spread, perView, pages, imgWidth, imgQ]);

  // Background warmer: quietly load the whole album (2 at a time) so every swipe is instant
  useEffect(() => {
    let cancelled = false;
    const urls = pages.filter((p) => p.type === "photo" && p.url).map((p) => optimizedImg(fileUrl(p.url), imgWidth, imgQ));
    const loadOne = (u) => new Promise((resolve) => {
      const im = new Image();
      im.onload = im.onerror = () => resolve();
      im.src = u;
    });
    const timer = setTimeout(async () => {
      for (let i = 0; i < urls.length && !cancelled; i += 2) {
        await Promise.all(urls.slice(i, i + 2).map(loadOne));
      }
    }, 1500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [pages, imgWidth, imgQ]);

  if (cats.length === 0) return null;
  const view = pages.slice(spread * perView, spread * perView + perView);
  const activeCat = view.map((p) => p?.category || p?.name).find(Boolean) ?? "";
  const enhanceCls = enhance ? "[filter:brightness(1.03)_contrast(1.06)_saturate(1.14)]" : "";

  const Flourish = () => (
    <div className="flex items-center gap-2.5 justify-center mt-5 md:mt-6">
      <span className="w-8 md:w-10 h-px bg-clay/60" />
      <span className="w-1.5 h-1.5 rotate-45 border border-clay/70" />
      <span className="w-8 md:w-10 h-px bg-clay/60" />
    </div>
  );

  const Page = ({ p }) => (
    <div className="flex-1 relative overflow-hidden min-w-0 bg-ivory bg-[radial-gradient(ellipse_at_top_left,rgba(158,123,79,0.06),transparent_60%)]">
      {!p ? (
        <div className="h-full flex items-center justify-center">
          <span className="font-display text-2xl text-ink/20 italic">{(endHeading ?? "").trim() || "fin."}</span>
        </div>
      ) : p.type === "cover" ? (
        <div className="h-full flex flex-col items-center justify-center text-center px-6 md:px-10">
          {settings?.logo_url && <LogoMark src={settings.logo_url} className="h-12 md:h-20 w-auto mb-5 md:mb-8" alt="Happy Retreats" />}
          <div className="overline accent-color">The Wedding Album</div>
          <div className="font-display text-2xl md:text-4xl lg:text-5xl mt-3 md:mt-5 text-ink leading-tight">{title || "Moments, chapter by chapter"}</div>
          <Flourish />
          <div className="text-[9px] md:text-[10px] uppercase tracking-[0.24em] text-graphite mt-5 md:mt-7">Curated by Happy Retreats</div>
        </div>
      ) : p.type === "chapter" ? (
        <div className="h-full flex flex-col items-center justify-center text-center px-5 md:px-12">
          <div className="overline accent-color text-[9px] md:text-[11px]">Chapter {roman(p.num)}</div>
          <div className="font-display text-xl md:text-4xl lg:text-5xl mt-2.5 md:mt-5 text-ink leading-snug">{p.name}</div>
          <div className="text-[9px] md:text-xs uppercase tracking-[0.2em] md:tracking-[0.22em] text-graphite mt-2 md:mt-4">{p.count} photograph{p.count === 1 ? "" : "s"}</div>
          {p.msg && <p className="font-display italic text-xs md:text-lg text-ink/70 leading-relaxed mt-3 md:mt-6 max-w-md line-clamp-6 md:line-clamp-none" data-testid="album-chapter-msg">{p.msg}</p>}
          <Flourish />
        </div>
      ) : p.type === "note" ? (
        <div className="h-full flex flex-col items-center justify-center text-center px-5 md:px-12">
          <div className="font-display text-4xl md:text-6xl accent-color leading-none">&ldquo;</div>
          <p className="font-display italic text-sm md:text-2xl text-ink/80 leading-relaxed max-w-md -mt-1 md:-mt-2 line-clamp-6 md:line-clamp-none" data-testid="album-note-msg">{p.msg}</p>
          <Flourish />
          <div className="text-[9px] md:text-[10px] uppercase tracking-[0.22em] md:tracking-[0.24em] text-graphite mt-4 md:mt-5">{p.name}</div>
        </div>
      ) : p.type === "end" ? (
        <div className="h-full flex flex-col items-center justify-center text-center px-6 md:px-10">
          {settings?.logo_url && <LogoMark src={settings.logo_url} className="h-10 md:h-16 w-auto mb-4 md:mb-6 opacity-80" alt="Happy Retreats" />}
          <span className="font-display italic text-xl md:text-3xl text-ink/60" data-testid="album-end-heading">{(endHeading ?? "").trim() || "fin."}</span>
          {(endMessage || "").trim() && (
            <p className="font-display italic text-sm md:text-lg text-ink/70 leading-relaxed mt-4 md:mt-6 max-w-md" data-testid="album-end-message">{endMessage.trim()}</p>
          )}
          <Flourish />
          <div className="text-[9px] md:text-[10px] uppercase tracking-[0.24em] text-graphite mt-5" data-testid="album-end-footer">{(endFooter ?? "").trim() || "happyretreats.in"}</div>
        </div>
      ) : (
        <PhotoPage p={p} enhanceCls={enhanceCls} imgWidth={imgWidth} imgQ={imgQ} />
      )}
    </div>
  );

  return (
    <div data-testid="album-book">
      {/* Preload upcoming photo pages so page-turns feel instant */}
      <div aria-hidden="true" style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", pointerEvents: "none" }}>
        {preloadUrls.map((u) => (
          <img key={u} src={u} alt="" loading="eager" decoding="async" />
        ))}
      </div>
      <div className="mb-6 md:mb-10 md:flex md:flex-wrap md:gap-2 -mx-4 md:mx-0 overflow-x-auto no-scrollbar md:overflow-visible flex gap-2 px-4 md:px-0">
        {cats.map((c, i) => (
          <button
            key={i}
            onClick={() => jumpToCat(c.name || "Untitled")}
            className={`shrink-0 px-3 md:px-4 py-1.5 md:py-2 text-[10px] md:text-[11px] uppercase tracking-[0.16em] md:tracking-[0.18em] border transition-colors ${
              activeCat === (c.name || "Untitled") ? "bg-ivory text-ink border-ivory" : "border-ivory/30 text-ivory/70 hover:border-ivory hover:text-ivory"
            }`}
            data-testid={`album-cat-chip-${i}`}
          >
            {c.name || "Untitled"}
          </button>
        ))}
      </div>

      <div className="relative [perspective:2200px]" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div
          key={turnKey}
          className="album-turn flex mx-auto max-w-5xl shadow-[0_60px_140px_-50px_rgba(0,0,0,0.9)] ring-1 ring-ivory/10"
          style={{ aspectRatio: isMobile ? "3/4" : "8/5", maxWidth: isMobile ? "420px" : undefined }}
          data-testid="album-spread"
        >
          <Page p={view[0]} />
          {!isMobile && (
            <>
              <div className="w-px bg-ink/25 relative z-10">
                <div className="absolute inset-y-0 -left-5 w-10 bg-gradient-to-r from-transparent via-ink/20 to-transparent pointer-events-none" />
              </div>
              <Page p={view[1]} />
            </>
          )}
        </div>
        <button
          onClick={() => go(-1)}
          disabled={spread === 0}
          aria-label="Previous page"
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-6 w-11 h-11 rounded-full bg-ivory text-ink shadow-lg flex items-center justify-center hover:bg-clay hover:text-ivory transition-colors disabled:opacity-30"
          data-testid="album-prev"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={() => go(1)}
          disabled={spread >= spreads - 1}
          aria-label="Next page"
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-6 w-11 h-11 rounded-full bg-ivory text-ink shadow-lg flex items-center justify-center hover:bg-clay hover:text-ivory transition-colors disabled:opacity-30"
          data-testid="album-next"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div className="max-w-5xl mx-auto mt-6" style={{ maxWidth: isMobile ? "420px" : undefined }}>
        <div className="h-px bg-ivory/15 relative overflow-hidden" data-testid="album-progress">
          <div className="absolute inset-y-0 left-0 bg-clay transition-all duration-500" style={{ width: `${((spread + 1) / spreads) * 100}%` }} />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mt-6 max-w-5xl mx-auto">
        <div className="text-[11px] uppercase tracking-[0.22em] text-ivory/60" data-testid="album-page-counter">
          {isMobile ? "Page" : "Spread"} {spread + 1} of {spreads}{isMobile ? " · swipe to turn" : ""}
        </div>
        <button
          onClick={requestDownload}
          disabled={downloading}
          className="inline-flex items-center gap-2.5 px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium bg-ivory text-ink hover:bg-clay hover:text-ivory transition-colors disabled:opacity-60"
          data-testid="album-download-pdf"
        >
          <FileDown className="w-4 h-4" /> {downloading ? "Binding your album…" : "Download album (PDF)"}
        </button>
      </div>
      <GuestDetailsDialog
        open={guestOpen}
        onClose={() => setGuestOpen(false)}
        onSubmit={(g) => { setGuestOpen(false); downloadPdf(g); }}
        heading="Almost there — who is this album for?"
        ctaLabel="Get my album"
      />
    </div>
  );
}
